/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react';
import { compose } from 'redux';
import { connect, useSelector } from 'react-redux';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { FullScreen } from 'react-full-screen';
import { isEmpty, isArray } from 'lodash';
import { makeStyles } from '@mui/styles';
import { Box } from '@mui/material';
import { useModal } from '@lwtears/lwt-common-frontend/lib/Modal';
import { UseModalDispatch, UseModalState } from '@lwtears/lwt-common-frontend/lib/Modal';
import withModal from '@lwtears/lwt-common-frontend/lib/Modal/withModal';
import DragContainer from '@lwtears/lwt-common-frontend/lib/DragContainer';
import { createEvent } from 'api';
import CopyrightModal from 'components/@common/Modals/CopyrightModal';
import DoublePageViewer from 'components/Workbook/DoublePageViewer';
import PageNavBar from 'components/PageNavBar/PageNavBar';
import PageSelectorMenu from 'components/PageSelectorMenu/PageSelectorMenu';
import ResourcesButton from 'components/@common/Buttons/ResourcesButton';
import ResourceModal from 'components/@common/Modals/ResourcePlayer';
import ResourcesSlider from 'components/Resources/ResourcesSlider';
import SinglePageViewer from 'components/Workbook/SinglePageViewer';
import ScreenOverlay from 'components/@common/ScreenOverlay';
import {
  BOOK_CODE_DESCRIPTIONS,
  TEACHER_GUIDE_DESCRIPTIONS,
  SETTING_TYPES,
  ProductCopyrightTerms,
  CopyrightTerms,
  AppStoreState,
  LiteResource,
  WorkbookState,
  PageDetail,
  Workbook as WorkbookType,
  Resource,
  Locale,
  UserSettingTypes,
  UserSetting
} from 'types';
import {
  setLastAccessed,
  getUsersWorkbooks,
  getPageDetails,
  batchWorkbookContentAndPDF
} from 'state/modules/entity/thunks/workbook';
import { saveUserSetting, saveProductAccess, saveCopyrightTerms } from 'state/modules/user/thunks';
import { changeAppLocale, selectResource, toggleResourcePlayerVisibility } from 'state/modules/app';
import { cancelDownload } from 'state/modules/entity';
import {
  getCurrentPage,
  getCurrentWorkbook
} from 'state/modules/entity/selectors/workbook-selectors';
import { hasClickAwayOnly, getLicenseIsTrial } from 'state/modules/user/selectors/user-selectors';
import { getTrialDaysRemaining } from 'state/modules/user/util';
import { toggleMenuByType } from 'state/modules/ui/actions';
import { mockedResource, useResources } from 'state/providers/ResourcesProvider';
import { useFullScreen } from 'state/providers/FullScreenProvider';
import { setLocale } from 'translations/translator';
import { getSwitchFrameType, isAVPlayer } from 'util/resources-util';
import withFtueTour from '../components/@common/enhancers/withFtueTour';
import { changePageNumberInPath } from '../util/url-util';
import { getClickAwayResourcesLink, getGroupedResources } from '../util/workbook-util';
import { MessagingState } from '../state/modules/messaging';
import { isCopyrightTermsViewed, showOnboardingModal } from '../state/modules/messaging';
import useIdleTimeout from 'state/hooks/use-idle-timer';
import { DEFAULT_ZOOM_SCALE, useZoom } from 'state/providers/ZoomProvider';
import WorkbookTour from './Tours/modules/Workbook';
import { getPlatform } from 'util/user-agent-util';

const BOTTOM_AND_TOP_NAV_HEIGHT = 135;

type StyleProps = {
  isIdle?: boolean;
  isOpen?: boolean;
  workbook?: WorkbookType;
};

type Props = {
  copyrightTerms: CopyrightTerms;
  getUsersWorkbooks: typeof getUsersWorkbooks;
  menuOpen: boolean;
  messaging: MessagingState;
  pdfScaleValue: number;
  saveProductAccess: typeof saveProductAccess;
  showOnboardingModal: typeof showOnboardingModal;
  showResourcePlayer: boolean;
  trialDaysLeft: number;
  pageData: PageDetail | Array<PageDetail>;
  pageMenuOpen: boolean;
  viewedWelcome: boolean;
  workbook: WorkbookState;
  cancelDownload: () => void;
  changeAppLocale: (locale: Locale) => void;
  getPageDetails: (bookCode: number, pageNumber: string | number, isZoom?: boolean | null) => void;
  saveUserSetting: (type: UserSettingTypes, setting: UserSetting) => void;
  selectResource: (resource: LiteResource) => void;
  selectedResource: any;
  setLastAccessed: (workbook: any) => void;
  togglePageSelectorMenu: (open?: boolean) => void;
  toggleResourcePlayerVisibility: (show: boolean) => void;
  id: number;
  saveCopyrightTerms: (productCopyrightTerms: ProductCopyrightTerms) => void;
  pdf: any;
  batchWorkbookContentAndPDF: any;
  pageDetails: any[];
  useStyles: () => { [key: string]: string };
  workbooks: any[];
} & UseModalDispatch &
  UseModalState;

const BOOK_CODES = Object.keys(BOOK_CODE_DESCRIPTIONS);
const VALID_BOOK_CODES = BOOK_CODES.concat(Object.keys(TEACHER_GUIDE_DESCRIPTIONS)) as any;

const Workbook = (props: Props) => {
  const {
    changeAppLocale,
    pageMenuOpen,
    trialDaysLeft,
    menuOpen,
    copyrightTerms,
    batchWorkbookContentAndPDF,
    saveProductAccess,
    getPageDetails,
    pdf,
    pageDetails
  } = props;

  const user = useSelector(({ user }: any) => user);
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { bookCode, edition, pageNumber, isZoom } = params;

  const viewedCopyrightTerms = useSelector(state => isCopyrightTermsViewed(state, params));
  const currentPage = useSelector(state => getCurrentPage(state, params));
  const currentWorkbook = useSelector(state => getCurrentWorkbook(state, params));
  const isClickAwayOnly = useSelector(state => hasClickAwayOnly(state, params));
  const isTrialLicense = useSelector(state => getLicenseIsTrial(state, params));

  const [{ isFullScreen, screen1Handle }, { setFullScreen }] = useFullScreen();
  const [{ isOpen }, { setResources, setIsOpen }] = useResources();
  const [{ isOpen: isModalOpen }, { closeModal }] = useModal();
  const [{ zoomScale }, { resetZoom }] = useZoom();
  const { isIdle } = useIdleTimeout({});
  const isOnePage = location.pathname.includes('/zoom');
  const userWorkbookTour = user.settings?.tours?.find(x => x.name === 'workbook');
  const shouldDoWorkbookTour = !!userWorkbookTour?.steps?.length;
  const isZoomedIn = zoomScale > DEFAULT_ZOOM_SCALE;

  const { id: currentWorkbookId, bookUrl, locale, products, totalPages, type } = currentWorkbook;

  const {
    presenterWrapper,
    fullScreenResourceWrapper,
    fullScreenResourceButton,
    fullScreenWrapper,
    workbookStyle,
    workbookCover
  } = useStyles({ workbook: currentWorkbook, isIdle, isOpen });

  const handleFullScreenChange = isFullScreen => {
    setFullScreen(isFullScreen);
  };

  const showCopyrightModal = () => {
    props.openModal({
      body: (
        <CopyrightModal
          currentWorkbook={currentWorkbook}
          handleDismiss={props.closeModal}
          saveCopyrightTerms={props.saveCopyrightTerms}
          showCheckbox={!copyrightTerms}
        />
      ),
      routeParams: { id: props.id }
    });
  };

  const shouldDisplayCopyrightModal = (): void => {
    if (VALID_BOOK_CODES.includes(bookCode) && !viewedCopyrightTerms && !menuOpen) {
      showCopyrightModal();
    }
  };

  const loadData = async (bookId: number, bookUrl: string) => {
    await batchWorkbookContentAndPDF(bookUrl, bookId);
    saveProductAccess(products?.[0]?.productCurriculumId);
    handlePageChange();
  };

  const handlePageChange = async () => {
    const pagesDetails = await getPageDetails(
      currentWorkbookId,
      pageNumber ?? '',
      isZoom === 'zoom'
    );
    if (!pageNumber) {
      setResources([]);
      return;
    }
    await getAndSetResources(pagesDetails);
    props.selectResource(mockedResource);

    createEvent('accessPage', {
      bookId: currentWorkbookId,
      section: type,
      pageNumber: parseInt(pageNumber, 10),
      pageLayout: isZoom ? 'singlePage' : 'doublePage',
      ...(pageDetails && pageDetails.length && { pageId: pageDetails?.[0].id })
    });
  };

  const getAndSetResources = (pagesDetails): void => {
    if (isEmpty(pagesDetails) || !isArray(pagesDetails)) return;

    const resources = getGroupedResources(pagesDetails);
    setResources(resources);
  };

  const handleSelectResource = async (resource: LiteResource) => {
    await props.selectResource(resource);
    handleOpenModal(resource);
  };

  const handleResourceClose = async () => {
    if (isModalOpen) closeModal();
    await props.selectResource(mockedResource);
  };

  const handleOpenModal = (resource: Resource) => {
    const frameType = getSwitchFrameType(resource.type?.type, resource?.data?.mediaType);
    const isAVPlayerResource = isAVPlayer(frameType);

    props.openDraggableModal({
      titleText: resource.title,
      body: (
        <ResourceModal
          selectedResource={resource}
          frameType={frameType}
          handleClose={handleResourceClose}
          eventLogger={() => null}
        />
      ),
      options: { closeOnBackdropClick: true },
      getContainerStyles: defaultStyles => ({
        ...defaultStyles,
        backgroundColor: '#fff'
      }),
      ...(isAVPlayerResource && {
        getPaperStyles: defaultStyles => ({
          ...defaultStyles,
          maxHeight: '85vh'
        })
      })
    });
  };

  const navigateIfInvalid = () => {
    if (bookCode) {
      if (!VALID_BOOK_CODES.includes(bookCode) || currentWorkbook?.bookCode !== bookCode) {
        navigate('/404');
        return;
      }
    }

    if (currentWorkbook?.bookCode && isClickAwayOnly) {
      navigate(getClickAwayResourcesLink(edition ?? '', bookCode ?? ''));
      return;
    }
  };

  useEffect(() => {
    resetZoom();
  }, [isFullScreen, isOnePage]);

  useEffect(() => {
    setIsOpen(false);
    navigateIfInvalid();
    shouldDisplayCopyrightModal();
  }, [bookCode]);

  useEffect(() => {
    if (!currentWorkbook?.bookCode) return;
    setLastAccessed(currentWorkbook);

    if (document.body) {
      document.body.style.background = currentWorkbook.secondaryColor;
    }
  }, [currentWorkbook]);

  useEffect(() => {
    if (!locale) return;
    setLocale(locale);
    saveUserSetting(SETTING_TYPES.LOCALE, locale);
    changeAppLocale(locale);
  }, [locale]);

  useEffect(() => {
    if (parseInt(pageNumber ?? '', 10) > parseInt(totalPages ?? '', 10)) {
      const newPath = changePageNumberInPath(
        { bookCode, edition: edition ?? '' } as any,
        totalPages ?? '',
        !!isZoom
      );

      navigate(newPath, { replace: true });
    }
    handlePageChange();
  }, [bookCode, edition, totalPages, pageNumber, isZoom]);

  useEffect(() => {
    getUsersWorkbooks();
  }, [trialDaysLeft, currentWorkbookId]);

  useEffect(() => {
    loadData(currentWorkbookId, bookUrl ?? '');
  }, [bookUrl, currentWorkbookId, pageNumber]);

  useEffect(() => {
    if (!pageNumber) return;
    const pageNum: number = parseInt(pageNumber ?? '', 10);
    if (pageNum && pageNum % 2 !== 0 && !isZoom) {
      navigate(`/books/${bookCode}/${edition}/${pageNum - 1}`);
    }
  }, [pageNumber]);

  const renderCover = () => {
    const translateAmount =
      zoomScale <= DEFAULT_ZOOM_SCALE ? 0 : zoomScale * (zoomScale > 1.5 ? 100 : 50);
    return (
      <div
        className={workbookStyle}
        style={{
          cursor: isZoomedIn ? 'inherit' : 'default'
        }}
      >
        <img
          className={workbookCover}
          src={currentWorkbook.coverImageUrl}
          alt={`${currentWorkbook.title} Cover`}
          style={{
            cursor: isZoomedIn ? 'inherit' : 'default',
            transform: `scale(${zoomScale}) translate(${translateAmount}px,${translateAmount}px)`
          }}
        />
      </div>
    );
  };

  const renderWorkbookContent = () => {
    const fingerprint = pdf?.pdfInfo ? pdf?.pdfInfo?.fingerprint : undefined;

    if (!pageNumber) return renderCover();

    if (isZoom) {
      return (
        <SinglePageViewer
          workbook={currentWorkbook}
          currentPage={currentPage}
          fingerprint={fingerprint}
        />
      );
    }

    return (
      <DoublePageViewer
        workbook={currentWorkbook}
        currentPage={currentPage}
        fingerprint={fingerprint}
      />
    );
  };

  const renderWorkbook = () => {
    if (!pageNumber) {
      return renderCover();
    }

    return <Box className={workbookStyle}>{renderWorkbookContent()}</Box>;
  };

  const platform = getPlatform();
  const isIos = platform.ios;

  return (
    <div
      style={{
        height: isFullScreen
          ? '100%'
          : `calc(100% - ${BOTTOM_AND_TOP_NAV_HEIGHT + (isIos ? 70 : 0)}px)`
      }}
    >
      {shouldDoWorkbookTour && <WorkbookTour />}
      <div className={presenterWrapper}>
        <FullScreen
          handle={screen1Handle}
          className={fullScreenWrapper}
          onChange={handleFullScreenChange}
        >
          {isFullScreen && (
            <div className={fullScreenResourceWrapper}>
              <ResourcesButton buttonClass={fullScreenResourceButton} />
              <ResourcesSlider
                workbook={currentWorkbook}
                handleSelectResource={handleSelectResource}
                currentPage={currentPage}
                isZoom={location.pathname.includes('/zoom')}
              />
            </div>
          )}
          <DragContainer isZoomedIn={isZoomedIn}>{renderWorkbook()}</DragContainer>
          <PageNavBar
            totalPages={totalPages}
            pageOffset={currentWorkbook.pageOffset}
            isZoom={location.pathname.includes('/zoom')}
            workbook={currentWorkbook}
            toggleMenu={props.togglePageSelectorMenu}
            isTrialLicense={isTrialLicense}
            trialDaysLeft={trialDaysLeft}
            showTrialModal={null}
            cancelDownloadRequest={cancelDownload}
            singlePage={isZoom}
          >
            <PageSelectorMenu
              show={pageMenuOpen}
              primaryBookColor={currentWorkbook.primaryColor}
              content={currentWorkbook.content}
              totalPages={totalPages}
              currentPageNumber={currentPage}
              toggleMenu={props.togglePageSelectorMenu}
              workbookCode={currentWorkbook.bookCode}
            />
          </PageNavBar>
        </FullScreen>
        <ScreenOverlay show={pageMenuOpen} onClick={props.togglePageSelectorMenu} zIndex={6} />
      </div>
      <ResourcesSlider
        workbook={currentWorkbook}
        handleSelectResource={handleSelectResource}
        currentPage={currentPage}
        isZoom={location.pathname.includes('/zoom')}
      />
    </div>
  );
};

const useStyles = makeStyles(() => ({
  presenterWrapper: ({ workbook }: StyleProps) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    backgroundColor: workbook?.secondaryColor,
    position: 'relative'
  }),
  workbookCover: {
    height: '90%',
    margin: '20px auto',
    userSelect: 'none',
    overflow: 'auto'
  },
  workbookStyle: ({ workbook }: StyleProps) => ({
    overflow: 'auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    width: '100%',
    position: 'relative',
    backgroundColor: workbook?.secondaryColor
  }),
  fullScreenResourceButton: ({ isIdle, isOpen, workbook }: StyleProps) => ({
    border: '1px solid black !important',
    background: workbook?.primaryColor,
    marginRight: '10px !important',
    position: 'absolute',
    right: -7,
    top: 30,
    transition: '0.5s',
    transform: !isIdle || isOpen ? 'translateX(0px)' : 'translateX(250px)'
  }),
  fullScreenResourceWrapper: {
    position: 'absolute',
    right: -7,
    top: 30,
    zIndex: 10
  },
  fullScreenWrapper: {
    height: '100%',
    width: '100% !important',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column'
  }
}));

const mapStateToProps = (state: AppStoreState) => ({
  copyrightTerms: state.entity.copyrightTerms,
  menuOpen: state.ui.menuOpen,
  messaging: state.messaging,
  pageMenuOpen: state.ui.pageSelectorMenuOpen,
  pdfScaleValue: state.app.pdfScaleValue,
  showResourcePlayer: state.app.resource.showResourcePlayer,
  trialDaysLeft: getTrialDaysRemaining(state.user),
  viewedWelcome: !!state.user.settings?.licensing,
  workbook: state.entity.workbooksById,
  workbooks: state.entity.workbooks,
  pdf: state.entity.pdf,
  pageDetails: state.entity.pageDetails,
  userId: state.user?.me?.id,
  selectedResource: state.app.resource.selectedResource
});

const withRedux = connect(mapStateToProps, {
  cancelDownload,
  changeAppLocale,
  getPageDetails,
  getUsersWorkbooks,
  batchWorkbookContentAndPDF,
  saveProductAccess,
  saveUserSetting,
  selectResource,
  setLastAccessed,
  showOnboardingModal,
  togglePageSelectorMenu: toggleMenuByType('pageSelector'),
  toggleResourcePlayerVisibility,
  saveCopyrightTerms
});
export default compose(
  withModal,
  withRedux,
  withFtueTour('workbook', 'workbookMediaButtons')
)(Workbook as any);
